// Finance ▸ Health & cash (CEO 2026-10-09) — the June health assessment,
// recomputed from live QuickBooks: real payroll, overhead without the one-time
// sponsorship, the current ratio both ways for the owner loan, and a rolling
// 13-week cash view from today with its assumptions written out.
import { cn } from "@/lib/utils";
import { FinanceHeader } from "./FinanceNav";
import { LiveGate, LiveToolbar, Panel, Stat, money, pct, ratio, shortDate, useLiveFinance, type HealthWindow, type LiveFinance } from "./shared";
import { ForecastAssumptions } from "./ForecastAssumptions";

export function HealthPage() {
  const { data, error, refreshing, reload, refetch } = useLiveFinance();
  const live = data?.connected ? data : null;
  return (
    <div className="mx-auto w-full max-w-[1180px] px-6 py-bay md:px-10">
      <FinanceHeader title="Health & cash" intro="The business health check, rebuilt on today's QuickBooks numbers, the next 13 weeks of cash, and what January needs. Every assumption is written out, and Jake can change them at the bottom." />
      <LiveGate data={data} error={error} onRetry={reload} />
      {live ? (
        <>
          <LiveToolbar data={live} refreshing={refreshing} onRefresh={reload} />
          <Profitability live={live} />
          <Liquidity live={live} />
          <Cashflow live={live} />
          <JanuaryReserve live={live} />
          <ForecastAssumptions weeks={live.cashflow?.weeks.map((w) => w.weekStart) ?? []} onSaved={refetch} />
        </>
      ) : null}
    </div>
  );
}

function WindowColumn({ w }: { w: HealthWindow }) {
  const rows: Array<[string, string, boolean?]> = [
    ["Revenue", money(w.revenue)],
    ["Cost of goods", money(w.cogs)],
    ["Gross profit", `${money(w.grossProfit)} · ${pct(w.grossMarginPct)}`, true],
    ["Payroll (wages, commissions, bonuses, employer tax)", money(w.payroll)],
    ["Overhead as booked", money(w.overhead)],
    ...w.oneTimeExcluded.map((o) => [`less one-time: ${o.label}`, `−${money(o.amount)}`] as [string, string]),
    ["Overhead, run rate", money(w.overheadRunRate), true],
    ["Net income as booked", `${money(w.netIncome)} · ${pct(w.netMarginPct)}`],
    ["Net income, run rate", money(w.netIncomeRunRate), true],
  ];
  return (
    <div>
      <p className="text-label font-semibold">{w.label}</p>
      <table className="mt-2 w-full text-meta tabular-nums">
        <caption className="sr-only">{w.label}</caption>
        <tbody>
          {rows.map(([k, v, strong]) => (
            <tr key={k} className="border-t border-rule">
              <th scope="row" className={cn("py-1.5 pr-3 text-left", strong ? "font-semibold" : "font-normal")}>{k}</th>
              <td className={cn("whitespace-nowrap py-1.5 text-right", strong && "font-semibold")}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Profitability({ live }: { live: LiveFinance }) {
  const h = live.health!;
  const ttm = h.ttm;
  const annualPayroll = ttm ? (ttm.payroll / ttm.months) * 12 : null;
  const annualNet = ttm ? (ttm.netIncomeRunRate / ttm.months) * 12 : null;
  return (
    <Panel title="Profitability" aside="Recomputed live — replaces the June estimates">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Gross margin, last 12 months" value={pct(ttm?.grossMarginPct)} />
        <Stat label="Payroll, per year" value={money(annualPayroll)} sub="from the wage accounts" />
        <Stat label="Net profit, per year (run rate)" value={money(annualNet)} tone={annualNet == null ? "neutral" : annualNet >= 0 ? "good" : "bad"} sub="before owner draws" />
        <Stat label="Net income this year" value={money(h.netIncomeYtdBalanceSheet)} sub={`balance sheet, at ${shortDate(h.liquidity.asOf)}`} />
      </div>
      <div className="mt-gutter grid gap-gutter md:grid-cols-2">
        {ttm ? <WindowColumn w={ttm} /> : null}
        {h.ytd ? <WindowColumn w={h.ytd} /> : null}
      </div>
      <div className="mt-4 text-meta text-ink-muted">
        {h.ownerDrawsYtd != null ? <p>Owner draws on the books this year: {money(h.ownerDrawsYtd)}.</p> : null}
        {h.notes.map((n) => <p key={n}>{n}</p>)}
      </div>
    </Panel>
  );
}

function Liquidity({ live }: { live: LiveFinance }) {
  const l = live.health!.liquidity;
  return (
    <Panel title={`Liquidity at ${shortDate(l.asOf)}`} aside="Shown both ways until the CPA decides how the owner loan is classified">
      <div className="grid gap-3 md:grid-cols-2">
        <div className={cn("rounded-lg border p-4", l.loanBookedAsCurrent === true ? "border-ink" : "border-rule")}>
          <p className="text-label font-semibold">Owner loan counted as current{l.loanBookedAsCurrent === true ? " (how it's booked now)" : ""}</p>
          <p className="mt-2 text-[24px] font-semibold tabular-nums">{ratio(l.ratioLoanCurrent)}</p>
          <p className="text-meta text-ink-muted">current ratio · working capital {money(l.workingCapitalLoanCurrent)}</p>
        </div>
        <div className={cn("rounded-lg border p-4", l.loanBookedAsCurrent === false ? "border-ink" : "border-rule")}>
          <p className="text-label font-semibold">Owner loan counted as long-term{l.loanBookedAsCurrent === false ? " (how it's booked now)" : ""}</p>
          <p className="mt-2 text-[24px] font-semibold tabular-nums">{ratio(l.ratioLoanLongTerm)}</p>
          <p className="text-meta text-ink-muted">current ratio · working capital {money(l.workingCapitalLoanLongTerm)}</p>
        </div>
      </div>
      <p className="mt-3 text-meta text-ink-muted">
        Current assets {money(l.currentAssets)} · current liabilities as booked {money(l.currentLiabilities)} · owner loan {money(l.ownerLoan)}.
        {live.health!.topCustomerSharePct != null ? ` Largest customer is ${pct(live.health!.topCustomerSharePct)} of what we're owed.` : ""}
      </p>
    </Panel>
  );
}

function Cashflow({ live }: { live: LiveFinance }) {
  const cf = live.cashflow!;
  if (!cf.weeks.length) {
    return <Panel title="Next 13 weeks of cash"><p className="text-body text-ink-muted">Can't forecast yet: {cf.missing.join(" ")}</p></Panel>;
  }
  const min = Math.min(0, ...cf.weeks.map((w) => w.endCash));
  const max = Math.max(cf.startCash ?? 0, ...cf.weeks.map((w) => w.endCash), 1);
  const cols: Array<[string, (w: (typeof cf.weeks)[number]) => number, 1 | -1]> = [
    ["Collections (owed now)", (w) => w.collectionsAr, 1],
    ["Collections (new sales)", (w) => w.collectionsNew, 1],
    ["Collections (your adjustments)", (w) => w.collectionsAdjust, 1],
    ["Suppliers (bills now)", (w) => w.suppliersAp, -1],
    ["Suppliers (new sales)", (w) => w.suppliersNew, -1],
    ["Credit cards", (w) => w.creditCards, -1],
    ["Payroll", (w) => w.payroll, -1],
    ["January bonus + employer tax", (w) => w.januaryBonus, -1],
    ["Year-end taxes", (w) => w.yearEndTaxes, -1],
    ["Recurring bills (yours)", (w) => w.recurringBills, -1],
    ["Overhead", (w) => w.overhead, -1],
    ["Owner draw", (w) => w.ownerDraw, -1],
  ];
  return (
    <Panel title="Next 13 weeks of cash" aside={`Start ${money(cf.startCash)} · lowest ${cf.lowest ? `${money(cf.lowest.endCash)} (week of ${shortDate(cf.lowest.weekStart)})` : "—"}`}>
      <div className="flex h-40 items-stretch gap-1.5" aria-hidden>
        {cf.weeks.map((w) => {
          const h = ((w.endCash - min) / (max - min)) * 100;
          return (
            <div key={w.weekStart} className="flex flex-1 flex-col justify-end" title={`Week of ${w.weekStart}: ${money(w.endCash)}`}>
              <div className={cn("w-full rounded-t-sm", w.endCash < 0 ? "bg-risk" : "bg-ink")} style={{ height: `${Math.max(h, 1)}%` }} />
            </div>
          );
        })}
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-right text-meta tabular-nums">
          <caption className="sr-only">13-week cash forecast by week</caption>
          <thead className="text-ink-muted">
            <tr><th scope="col" className="sticky left-0 bg-white py-2 pr-3 text-left font-semibold">Week of</th>{cf.weeks.map((w) => <th key={w.weekStart} scope="col" className="whitespace-nowrap px-2 py-2 font-semibold">{w.weekStart.slice(5)}</th>)}</tr>
          </thead>
          <tbody>
            {cols.filter(([, fn]) => cf.weeks.some((w) => fn(w) !== 0)).map(([label, fn, sign]) => (
              <tr key={label} className="border-t border-rule">
                <th scope="row" className="sticky left-0 whitespace-nowrap bg-white py-1.5 pr-3 text-left font-normal">{label}</th>
                {cf.weeks.map((w) => { const v = fn(w); return <td key={w.weekStart} className="whitespace-nowrap px-2 py-1.5">{v ? money(sign * v) : ""}</td>; })}
              </tr>
            ))}
            <tr className="border-t border-ink"><th scope="row" className="sticky left-0 bg-white py-1.5 pr-3 text-left font-semibold">Net</th>{cf.weeks.map((w) => <td key={w.weekStart} className={cn("whitespace-nowrap px-2 py-1.5 font-semibold", w.net < 0 && "text-risk")}>{money(w.net)}</td>)}</tr>
            <tr className="border-t border-rule"><th scope="row" className="sticky left-0 bg-white py-1.5 pr-3 text-left font-semibold">Cash at week end</th>{cf.weeks.map((w) => <td key={w.weekStart} className={cn("whitespace-nowrap px-2 py-1.5 font-semibold", w.endCash < 0 && "text-risk")}>{money(w.endCash)}</td>)}</tr>
          </tbody>
        </table>
      </div>
      {cf.beyondWindow.length ? (
        <p className="mt-4 rounded border border-rule bg-paper px-4 py-3 text-body">
          Just after week 13: {cf.beyondWindow.map((e) => `${e.label} ${money(e.amount)} on ${shortDate(e.date)}`).join("; ")}.
          {" "}Cash at the end of week 13 less these: <b className={cn((cf.cashAfterBeyond ?? 0) < 0 && "text-risk")}>{money(cf.cashAfterBeyond)}</b> (before anything else that lands after week 13).
        </p>
      ) : null}
      <details className="mt-4">
        <summary className="cursor-pointer text-label font-semibold">How this forecast is built</summary>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-meta text-ink-muted">
          {cf.assumptions.map((a) => <li key={a}>{a}</li>)}
          {cf.excludedAr91Plus ? <li>Not counted: {money(cf.excludedAr91Plus)} owed for more than 90 days.</li> : null}
        </ul>
      </details>
    </Panel>
  );
}

function JanuaryReserve({ live }: { live: LiveFinance }) {
  const r = live.reserve;
  if (!r) return null;
  const covered = r.needed > 0 && r.held >= r.needed;
  const check = live.checks?.tax_reserve;
  return (
    <Panel title="January reserve" aside="Open item 16 · from the compensation structure">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="January needs" value={money(r.needed)} sub={`by ${shortDate(r.bonusDate)}`} />
        <Stat label="In the reserve account" value={r.accounts.length ? money(r.held) : "No account"} sub={r.accounts.map((a) => a.name).join(", ") || "none in QuickBooks yet"} tone={covered ? "good" : "bad"} />
        <Stat label="Still to set aside" value={money(r.gap)} tone={r.gap > 0 ? "bad" : "good"} />
        <Stat label="Per week until then" value={money(r.perWeek)} sub={`${r.weeksToBonus} weeks left`} />
      </div>
      <table className="mt-4 w-full max-w-[560px] text-meta tabular-nums">
        <caption className="sr-only">What the January reserve covers</caption>
        <tbody>
          <tr className="border-t border-rule"><th scope="row" className="py-1.5 pr-3 text-left font-normal">Kat's January bonus (annual + Q4 + December tiers)</th><td className="py-1.5 text-right">{money(r.bonus)}</td></tr>
          <tr className="border-t border-rule"><th scope="row" className="py-1.5 pr-3 text-left font-normal">Employer payroll tax on it</th><td className="py-1.5 text-right">{money(r.employerTax)}</td></tr>
          <tr className="border-t border-rule"><th scope="row" className="py-1.5 pr-3 text-left font-normal">Year-end taxes{r.yearEndTaxesSet ? ` (due ${shortDate(r.taxesDate)})` : ""}</th><td className="py-1.5 text-right">{r.yearEndTaxesSet ? money(r.yearEndTaxes) : <span className="text-risk">Not set — add it in the assumptions</span>}</td></tr>
          <tr className="border-t border-ink font-semibold"><th scope="row" className="py-1.5 pr-3 text-left">Reserve needed</th><td className="py-1.5 text-right">{money(r.needed)}</td></tr>
        </tbody>
      </table>
      {check ? <p className="mt-3 text-meta text-ink-muted">QuickBooks: {check.summary}</p> : null}
      <details className="mt-4">
        <summary className="cursor-pointer text-label font-semibold">How the bonus is sized</summary>
        <div className="mt-2 max-w-[72ch] space-y-1 text-meta text-ink-muted">
          <p>Last January Kat was paid {money(r.tiers.lastJanuary.amount)} on {shortDate(r.tiers.lastJanuary.paid)} — that's the default here. Annual, fourth-quarter and December bonuses all pay at the start of January, so they land together; the most the tiers can stack to is {money(r.maxStack)}.</p>
          <p>Annual: {r.tiers.annual.map(([rev, b]) => `${money(rev)} → ${money(b)}`).join(" · ")}</p>
          <p>Quarterly: {r.tiers.quarterly.map(([rev, b]) => `${money(rev)} → ${money(b)}`).join(" · ")}</p>
          <p>Monthly: {r.tiers.monthly.map(([rev, b]) => `${money(rev)} → ${money(b)}`).join(" · ")}</p>
          <p>The reserve account is any active bank or asset account with "reserve" in its name. If it's a bank account, its balance is also inside the forecast's starting cash.</p>
        </div>
      </details>
    </Panel>
  );
}
